import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbGlobalLogicalPosition, NbToastrService } from '@nebular/theme';
import { Subscription, interval, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ChatPerguntaRespostaService } from '../../../pages/negocio/cliente/components/chat-pergunta-resposta/chat-pergunta-resposta.service';
import { NegocioDataService } from '../../../pages/negocio/shared/services/NegocioController/negocio-data.services';
import { ListaCliente } from '../../../shared/Operations/listaCliente';
import { RegistrarAcessoService } from '../../../shared/services/registrar-acesso';

interface AlertaChat {
  conversaId: string;
  clienteId: string;
  nomeCliente: string;
  titulo: string;
  nomeExecutivo: string;
  nomeOperacional: string;
  motivo: string;
  referencia: string;
  atrasoMinutos: number;
}

@Component({
  selector: 'ngx-alerta-chat-operacional',
  templateUrl: './alerta-chat-operacional.component.html',
  styleUrls: ['./alerta-chat-operacional.component.scss']
})
export class AlertaChatOperacionalComponent implements OnInit, OnDestroy {
  aberto = false;
  alertas: AlertaChat[] = [];
  clienteLinkCarregandoId: string | null = null;
  private readonly posicao = NbGlobalLogicalPosition.TOP_END;
  private readonly chaveSessao = 'alerta-chat-apresentados';
  private readonly intervaloConsultaMs = 60_000;
  private assinatura?: Subscription;
  private fechando = false;

  constructor(
    private api: ChatPerguntaRespostaService,
    private negocioDataService: NegocioDataService,
    private registrarAcessoService: RegistrarAcessoService,
    private toastr: NbToastrService
  ) {}

  ngOnInit(): void {
    if (!sessionStorage.getItem('usuarioId')) {
      return;
    }
    // O prazo de cada pergunta fica na API. Aqui só consulta de minuto em minuto.
    // Depois do "Entendi", a API segura o mesmo aviso pelo prazo e devolve de novo se continuar parado.
    this.carregar();
    this.assinatura = interval(this.intervaloConsultaMs).subscribe(() => this.carregar());
  }

  ngOnDestroy(): void {
    this.assinatura?.unsubscribe();
  }

  get alertasNovas(): AlertaChat[] {
    return this.alertas.filter(alerta => this.motivoDe(alerta) === 'N');
  }

  get alertasPendenteRetorno(): AlertaChat[] {
    return this.alertas.filter(alerta => this.motivoDe(alerta) === 'E');
  }

  get alertasSemRetorno(): AlertaChat[] {
    return this.alertas.filter(alerta => this.motivoDe(alerta) === 'O');
  }

  motivoDe(alerta: AlertaChat): string {
    const motivo = String(alerta.motivo || '').trim().toUpperCase();
    if (motivo === 'E' || motivo === 'A') {
      return 'E';
    }
    if (motivo === 'O') {
      return 'O';
    }
    return 'N';
  }

  rotuloMotivo(alerta: AlertaChat): string {
    const motivo = this.motivoDe(alerta);
    if (motivo === 'E') {
      return 'Pendente de retorno';
    }
    if (motivo === 'O') {
      return 'Sem retorno do executivo ou conversa não encerrada';
    }
    return 'Criada pelo executivo e ainda não iniciada';
  }

  textoAtraso(alerta: AlertaChat): string {
    const minutos = Number(alerta.atrasoMinutos);
    if (!Number.isFinite(minutos) || minutos < 1) {
      return 'O prazo acabou de estourar';
    }
    return `O prazo estourou ${this.formatarDuracao(Math.floor(minutos))}`;
  }

  private formatarDuracao(minutos: number): string {
    if (minutos < 60) {
      return minutos === 1 ? 'há 1 minuto' : `há ${minutos} minutos`;
    }
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    if (horas < 24) {
      const hora = horas === 1 ? '1 hora' : `${horas} horas`;
      return resto ? `há ${hora} e ${resto} min` : `há ${hora}`;
    }
    const dias = Math.floor(horas / 24);
    const horasRestantes = horas % 24;
    const dia = dias === 1 ? '1 dia' : `${dias} dias`;
    return horasRestantes ? `há ${dia} e ${horasRestantes} h` : `há ${dia}`;
  }

  desde(alerta: AlertaChat): string {
    const data = new Date(alerta.referencia);
    if (Number.isNaN(data.getTime())) {
      return '';
    }
    return data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  abrir(alerta: AlertaChat): void {
    if (!alerta.clienteId || this.clienteLinkCarregandoId) {
      return;
    }
    this.clienteLinkCarregandoId = alerta.conversaId;
    this.buscarNegocio(alerta.clienteId).subscribe(negocio => {
      this.clienteLinkCarregandoId = null;
      if (!negocio) {
        this.toastr.warning('Não retornou dados', 'Atenção', { position: this.posicao });
        return;
      }
      this.removerAlertas([alerta]);
      this.marcarVisto([alerta]);
      const cliente = this.mapearClienteNegocio(negocio);
      this.gravarCliente(window, cliente);
      this.registrarAcessoService.registerUserIP(cliente);
      this.aberto = this.alertas.length > 0;
      window.location.href = `${environment.siteUrl}/pages/negocio/cliente/editar`;
    });
  }

  dispensar(): void {
    if (this.fechando) {
      return;
    }
    this.fechando = true;
    const snapshot = [...this.alertas];
    this.alertas = [];
    this.aberto = false;
    this.marcarVisto(snapshot, () => {
      this.fechando = false;
    });
  }

  private carregar(): void {
    this.api.listarAlertasPendentes().subscribe({
      next: resposta => {
        const dados = resposta?.data ?? resposta?.Data;
        const lista = Array.isArray(dados) ? dados : [];
        const recebidos = lista.map((item: any) => ({
          conversaId: String(item?.ConversaId ?? item?.conversaId ?? ''),
          clienteId: String(item?.ClienteId ?? item?.clienteId ?? ''),
          nomeCliente: String(item?.NomeCliente ?? item?.nomeCliente ?? 'Cliente'),
          titulo: String(item?.Titulo ?? item?.titulo ?? 'Pergunta'),
          nomeExecutivo: String(item?.NomeExecutivo ?? item?.nomeExecutivo ?? 'Executivo'),
          nomeOperacional: String(item?.NomeOperacional ?? item?.nomeOperacional ?? '').trim(),
          motivo: String(item?.Motivo ?? item?.motivo ?? 'N'),
          referencia: String(item?.Referencia ?? item?.referencia ?? ''),
          atrasoMinutos: Number(item?.AtrasoMinutos ?? item?.atrasoMinutos ?? 0)
        })).filter((item: AlertaChat) => !!item.conversaId);
        const jaMostrados = this.apresentados();
        const chavesRecebidas = new Set(recebidos.map(item => this.chave(item)));
        let esqueceu = false;
        jaMostrados.forEach(chave => {
          if (!chavesRecebidas.has(chave)) {
            jaMostrados.delete(chave);
            esqueceu = true;
          }
        });
        if (esqueceu) {
          sessionStorage.setItem(this.chaveArmazenada(), JSON.stringify([...jaMostrados]));
        }
        const novos = recebidos.filter(item => !this.aberto || !jaMostrados.has(this.chave(item)));
        if (!novos.length) {
          return;
        }
        this.alertas = this.aberto ? this.juntar(this.alertas, novos) : novos;
        this.aberto = this.alertas.length > 0;
        this.registrarApresentados(novos);
      },
      error: () => undefined
    });
  }

  private marcarVisto(alertas: AlertaChat[], aoFinalizar?: () => void): void {
    const itens = alertas.map(alerta => ({
      conversaId: alerta.conversaId,
      motivo: this.motivoDe(alerta),
      referencia: this.referenciaParaApi(alerta.referencia)
    }));
    if (!itens.length) {
      aoFinalizar?.();
      return;
    }
    this.api.marcarAlertaVisto(itens).subscribe({
      next: () => aoFinalizar?.(),
      error: () => aoFinalizar?.()
    });
  }

  private referenciaParaApi(referencia: string): string {
    const data = new Date(referencia);
    if (Number.isNaN(data.getTime())) {
      return new Date().toISOString();
    }
    return data.toISOString();
  }

  private chave(alerta: AlertaChat): string {
    return `${alerta.conversaId}|${this.motivoDe(alerta)}|${alerta.referencia}`;
  }

  private chaveArmazenada(): string {
    const usuario = sessionStorage.getItem('usuarioId') || 'anon';
    return `${this.chaveSessao}:${usuario}`;
  }

  private apresentados(): Set<string> {
    try {
      const bruto = sessionStorage.getItem(this.chaveArmazenada());
      const lista = bruto ? JSON.parse(bruto) : [];
      return new Set(Array.isArray(lista) ? lista.map((item: unknown) => String(item)) : []);
    } catch {
      return new Set();
    }
  }

  private registrarApresentados(alertas: AlertaChat[]): void {
    const vistos = this.apresentados();
    alertas.forEach(alerta => vistos.add(this.chave(alerta)));
    sessionStorage.setItem(this.chaveArmazenada(), JSON.stringify([...vistos]));
  }

  private juntar(atuais: AlertaChat[], novos: AlertaChat[]): AlertaChat[] {
    const chaves = new Set(atuais.map(alerta => this.chave(alerta)));
    return [...atuais, ...novos.filter(alerta => !chaves.has(this.chave(alerta)))];
  }

  private removerAlertas(remover: AlertaChat[]): void {
    const ids = new Set(remover.map(item => item.conversaId.toLowerCase()));
    this.alertas = this.alertas.filter(item => !ids.has(item.conversaId.toLowerCase()));
  }

  private buscarNegocio(clienteId: string) {
    return this.negocioDataService.CarregarNegociosCliente(clienteId).pipe(
      switchMap(value => {
        const negocios = value?.data ?? value?.Data;
        const success = value?.success ?? value?.Success;
        if (success && Array.isArray(negocios) && negocios.length > 0) {
          return of(negocios[0]);
        }
        return this.negocioDataService.GetPrimeiroNegocioDoClient(clienteId).pipe(
          map(result => this.negocioDe(result))
        );
      }),
      catchError(() => this.negocioDataService.GetPrimeiroNegocioDoClient(clienteId).pipe(
        map(result => this.negocioDe(result)),
        catchError(() => of(null))
      ))
    );
  }

  private negocioDe(result: any): any {
    const negocio = result?.data ?? result?.Data;
    const ok = result?.success ?? result?.Success;
    return ok && negocio ? negocio : null;
  }

  private gravarCliente(destino: Window, cliente: ListaCliente): void {
    try {
      destino.sessionStorage.setItem('cliente', JSON.stringify(cliente));
    } catch {
      sessionStorage.setItem('cliente', JSON.stringify(cliente));
    }
  }

  private mapearClienteNegocio(negocio: any): ListaCliente {
    return {
      idNegocio: negocio.id,
      idCliente: negocio.Client.id,
      nomeCliente: negocio.Client.name,
      cpfCnpj: negocio.Client.document_Number,
      analista: negocio.Employee.employeeName,
      dadosUsuario: negocio.Employee,
      dataNascimento: negocio.Client.birthDate != null ? negocio.Client.birthDate : null,
      profissao: negocio.Client.profession != null ? negocio.Client.profession : '',
      endereco: negocio.Client.address != null ? negocio.Client.address : '',
      cidade: negocio.Client.city != null ? negocio.Client.city : '',
      estado: negocio.Client.state != null ? negocio.Client.state : '',
      dataObito: negocio.Client.deathDate != null ? negocio.Client.deathDate : null,
      cadastro: negocio.company != null ? negocio.company : '',
      situacaoCpfCnpj: negocio.Client.ClientSituation != null && negocio.Client.ClientSituation.code != null ? negocio.Client.ClientSituation.code.toString() : null,
      dadoObito: negocio.Client.deathData != null ? negocio.Client.deathData : '',
      negocio,
      situacaoNegocio: '',
      analistaFeedback: '',
      dtContato: null,
      dtRetorno: null,
      retornarEm: null,
      obito: negocio.Client.obito,
      feedback: null,
      analistaCompartilhado: negocio.AnalistaCompartilhado,
      valorAtual: null,
      dtOutroRetorno: null,
      dataNegocioFechado: null,
      idAnalista: negocio.Employee.Id,
      porcentagemReno: negocio.closedPercentage != null ? negocio.closedPercentage : null,
      valorPrevisaoReno: negocio.valorPrevisaoReno != null ? negocio.valorPrevisaoReno : null,
      temHerdeiro: negocio.TemHerdeiro,
      tipoConhecimentoAtivo: negocio.TipoConhecimentoAtivos != null ? negocio.TipoConhecimentoAtivos.codigo : null,
      valorSaldo: negocio.ValorSaldo != null ? negocio.ValorSaldo : null,
      valorAtualNegocio: (negocio.valorAtual != null ? negocio.valorAtual : 0) + (negocio.ValorSaldo != null ? negocio.ValorSaldo : 0),
      temHerdeiroComPagamento: negocio.PagamentoEfetuado != null ? negocio.PagamentoEfetuado : false,
      faseNegocio: negocio.FaseNegocio != null ? negocio.FaseNegocio : null,
      previsaoCaixa: null
    };
  }
}
